import * as React from 'react';
import {
    GroupedComboBox,
    GroupedComboBoxTooltipFunction,
    IGroupedComboBoxItem,
} from 'grouped-combobox-web';
import {
    AppContext,
    GroupedComboBoxRenderers,
    DefaultStringResources,
    DefaultDropDownWidth,
    useFontPickerCore,
    iconNames,
    getFontAndPinnedState,
    getFontTooltip,
} from 'modern-font-picker-core';
import { IFontPickerProps } from './FontPicker.types';
import renderLabelWithPreviewFont from './renderLabelWithPreviewFont';
import renderLabelWithOwnFont from './renderLabelWithOwnFont';
import { createRenderIconCallback } from '../PinActionButton/renderIcon';
import { FontIcon, getTheme, TooltipHost } from '@fluentui/react';
import { missingFontIconStyle } from './FontPicker.styles';

export const FontPicker = React.forwardRef<HTMLDivElement, IFontPickerProps>(
    (props: IFontPickerProps, ref) => {
        const {
            isSubscriber,
            selectedFont,
            stringResources = DefaultStringResources,
            iconResources,
            previewFontFamily,
            dropDownWidth = DefaultDropDownWidth,
            menuInitiallyOpened,
            styles,
            allowFontInfoPane = true,
            allowPinning = true,
            ...rest
        } = props;

        const currentTheme = props.theme || getTheme();

        const actionButtonStyles =
            typeof styles === 'function'
                ? styles({ theme: currentTheme, width: props.width })?.subComponentStyles
                      ?.actionButtonStyles
                : styles?.subComponentStyles?.actionButtonStyles;

        const renderers = React.useMemo<GroupedComboBoxRenderers>(
            () => ({
                renderHeaderLabel: undefined,
                renderIcon: createRenderIconCallback(
                    isSubscriber ?? false,
                    stringResources,
                    allowFontInfoPane,
                    allowPinning,
                    actionButtonStyles
                ),
                renderOwnFontLabel: renderLabelWithOwnFont,
                renderPreviewFontLabel: (ps): React.ReactNode =>
                    renderLabelWithPreviewFont(ps, previewFontFamily),
            }),
            [
                isSubscriber,
                stringResources,
                allowFontInfoPane,
                allowPinning,
                actionButtonStyles,
                previewFontFamily,
            ]
        );

        const [showingItemPreview, setShowingItemPreview] = React.useState<boolean>(false);

        const [
            comboBox,
            groups,
            appContext,
            menuOpenedCallback,
            menuDismissedCallback,
            onItemClickCallback,
            onItemSecondaryActionCallback,
            onItemPreviewCallback,
            onFontChangeCallback,
            onAnnounceSearchResult,
        ] = useFontPickerCore(props, renderers, setShowingItemPreview);

        const isMissingFont = selectedFont?.isMissingFont;
        const missingIconName = iconResources?.missingIconInTextFieldName;
        const missingFontTooltip = stringResources.missingFontTooltip;
        const onRenderTooltip = props.onRenderTooltip;

        const onRenderMissingIcon = React.useCallback(() => {
            const missingIcon = (
                <FontIcon
                    style={missingFontIconStyle}
                    iconName={missingIconName ?? iconNames.warningInTextField}
                />
            );

            if (onRenderTooltip) {
                return onRenderTooltip({
                    mainContent: missingIcon,
                    tooltip: missingFontTooltip,
                    showTooltipOnRight: false,
                });
            } else {
                return <TooltipHost content={missingFontTooltip}>{missingIcon}</TooltipHost>;
            }
        }, [missingIconName, missingFontTooltip, onRenderTooltip]);

        const getItemTooltipText = React.useCallback<GroupedComboBoxTooltipFunction>(
            (item: IGroupedComboBoxItem, isKeyFocused: boolean): string => {
                const [font] = getFontAndPinnedState(item);
                return getFontTooltip(
                    font,
                    stringResources,
                    isSubscriber ?? false,
                    isKeyFocused,
                    allowFontInfoPane,
                    allowPinning
                );
            },
            [stringResources, isSubscriber, allowFontInfoPane, allowPinning]
        );

        const enableTextFieldMissingIcon =
            isMissingFont &&
            isSubscriber !== false && // isSubscriber may be undefined on load so we wait until we know it's false to disable the missing icon
            !showingItemPreview;

        return (
            <AppContext.Provider value={appContext}>
                <GroupedComboBox
                    {...rest}
                    ref={ref}
                    componentRef={comboBox}
                    groups={groups}
                    enableFiltering
                    selectedItem={selectedFont}
                    onAnnounceSearchResult={onAnnounceSearchResult}
                    onMenuOpened={menuOpenedCallback}
                    onMenuDismissed={menuDismissedCallback}
                    onItemClick={onItemClickCallback}
                    onItemSecondaryAction={onItemSecondaryActionCallback}
                    onItemPreview={onItemPreviewCallback}
                    onChange={onFontChangeCallback}
                    onRenderTextFieldPrefix={
                        enableTextFieldMissingIcon ? onRenderMissingIcon : undefined
                    }
                    getItemTooltip={getItemTooltipText}
                    iconResources={iconResources}
                    dropDownWidth={dropDownWidth}
                    styles={styles}
                    menuInitiallyOpened={menuInitiallyOpened}
                />
            </AppContext.Provider>
        );
    }
);

FontPicker.displayName = 'FontPicker';
